/**
 * Claude Hook系统
 * 为AI提供环境辅助，在关键时刻自动执行操作
 */

import { EventEmitter } from 'events';
import fs from 'fs-extra';
import path from 'path';
import { exec } from 'child_process';
import { promisify } from 'util';
import { logger } from '../utils/logger.js';
import { getWorkflowRoot } from '../utils/path.js';

const execAsync = promisify(exec);

export interface HookContext {
  type: string;
  timestamp: Date;
  data?: any;
  tool?: string;
  file?: string;
  error?: Error;
}

export interface Hook {
  name: string;
  enabled: boolean;
  timeout?: number;
  description?: string;
}

export interface HookConfig {
  hooks: Record<string, Hook>;
  globalTimeout?: number;
  logEnabled?: boolean;
}

export class ClaudeHookSystem extends EventEmitter {
  private config: HookConfig;
  private claudeDir: string;
  private hooksDir: string;
  private logFile: string;

  constructor() {
    super();
    const workflowRoot = getWorkflowRoot();
    this.claudeDir = path.join(workflowRoot, '.claude');
    this.hooksDir = path.join(this.claudeDir, 'hooks');
    this.logFile = path.join(this.claudeDir, 'hooks.log');
    
    // 默认配置
    this.config = {
      hooks: {
        'before_session': {
          name: 'before_session',
          enabled: true,
          timeout: 5000,
          description: 'AI会话开始前执行，注入上下文'
        },
        'after_tool_use': {
          name: 'after_tool_use',
          enabled: true,
          timeout: 3000,
          description: '工具使用后执行，如代码检查'
        },
        'on_task_update': {
          name: 'on_task_update',
          enabled: true,
          timeout: 2000,
          description: '任务状态更新时执行'
        },
        'on_file_change': {
          name: 'on_file_change',
          enabled: true,
          timeout: 2000,
          description: '文件变更时执行'
        },
        'on_error': {
          name: 'on_error',
          enabled: true,
          timeout: 1000,
          description: '错误发生时执行'
        },
        'after_session': {
          name: 'after_session',
          enabled: true,
          timeout: 3000,
          description: '会话结束后执行，保存上下文'
        }
      },
      globalTimeout: 10000,
      logEnabled: true
    };
  }

  /**
   * 初始化Hook系统
   */
  async initialize(): Promise<void> {
    try {
      // 确保目录存在
      await fs.ensureDir(this.hooksDir);
      await fs.ensureDir(path.join(this.claudeDir, 'logs'));
      
      // 加载配置
      await this.loadConfig();
      
      // 创建默认hooks（如果不存在）
      await this.createDefaultHooks();
      
      logger.info('🪝 Claude Hook系统初始化成功');
    } catch (error) {
      logger.error('Hook系统初始化失败:', error);
      throw error;
    }
  }

  /**
   * 执行Hook
   */
  async executeHook(hookName: string, context: HookContext): Promise<void> {
    const hook = this.config.hooks[hookName];
    if (!hook || !hook.enabled) {
      return;
    }

    const startTime = Date.now();
    
    try {
      await this.log(`执行Hook: ${hookName}`, context);
      
      const scriptPath = path.join(this.hooksDir, hookName);
      if (!(await fs.pathExists(scriptPath))) {
        await this.log(`Hook脚本不存在: ${scriptPath}`);
        return;
      }

      // 准备环境变量
      const env = {
        ...process.env,
        HOOK_NAME: hookName,
        HOOK_CONTEXT: JSON.stringify(context),
        WORKFLOW_ROOT: getWorkflowRoot(),
        CLAUDE_DIR: this.claudeDir
      };

      // 添加特定上下文的环境变量
      if (context.tool) env.TOOL_NAME = context.tool;
      if (context.file) env.FILE_PATH = context.file;
      if (context.error) env.ERROR_MESSAGE = context.error.message;

      // 执行脚本
      const timeout = hook.timeout || this.config.globalTimeout || 10000;
      const { stdout, stderr } = await execAsync(`bash ${scriptPath}`, {
        env,
        timeout,
        cwd: getWorkflowRoot()
      });

      if (stdout) {
        await this.log(`Hook输出: ${stdout.trim()}`);
      }
      if (stderr) {
        await this.log(`Hook警告: ${stderr.trim()}`);
      }

      const duration = Date.now() - startTime;
      await this.log(`Hook执行成功，耗时: ${duration}ms`);
      
      this.emit('hook:executed', { hookName, context, duration });
    } catch (error: any) {
      const duration = Date.now() - startTime;
      await this.log(`Hook执行失败: ${error.message}`, { error });
      
      // 不要让Hook失败影响主流程
      this.emit('hook:error', { hookName, context, error, duration });
    }
  }

  /**
   * 创建默认Hook脚本
   */
  private async createDefaultHooks(): Promise<void> {
    const defaultHooks = {
      'before_session': `#!/bin/bash
# AI会话开始前执行
# 生成上下文，检查任务状态，提供建议

echo "🚀 正在准备AI工作环境..."

# 生成最新上下文
workflow context generate --quiet

# 检查当前任务
CURRENT_TASK=$(workflow now --json 2>/dev/null)
if [ -n "$CURRENT_TASK" ]; then
  echo "📋 当前任务："
  workflow now
  echo ""
  echo "💡 建议："
  workflow suggest next
else
  echo "💡 没有进行中的任务，推荐下一个："
  workflow next --preview
fi

echo ""
echo "✅ 环境准备完成！"`,

      'after_tool_use': `#!/bin/bash
# 工具使用后执行
# 根据不同工具执行不同操作

case "$TOOL_NAME" in
  "Edit"|"Write")
    if [ -n "$FILE_PATH" ]; then
      # 对于代码文件，执行lint
      if [[ "$FILE_PATH" =~ \.(ts|tsx|js|jsx)$ ]]; then
        echo "🔍 检查代码规范..."
        workflow lint "$FILE_PATH" --quiet || true
      fi
    fi
    ;;
  "Bash")
    # 命令执行后，检查是否需要更新上下文
    if [[ "$HOOK_CONTEXT" =~ "npm install" ]] || [[ "$HOOK_CONTEXT" =~ "pnpm install" ]]; then
      echo "📦 检测到依赖变更，更新上下文..."
      workflow context update --dependencies
    fi
    ;;
esac`,

      'on_task_update': `#!/bin/bash
# 任务更新时执行

echo "📝 任务状态已更新"

# 如果任务完成，自动提交
if [[ "$HOOK_CONTEXT" =~ '"status":"done"' ]]; then
  echo "🎉 任务完成！准备提交代码..."
  workflow commit --auto --quiet || true
  
  echo "💡 推荐下一个任务："
  workflow next --preview
fi`,

      'on_error': `#!/bin/bash
# 错误发生时执行

echo "❌ 检测到错误：$ERROR_MESSAGE"
echo "💡 尝试自动修复建议："

# 根据错误类型提供建议
if [[ "$ERROR_MESSAGE" =~ "Cannot find module" ]]; then
  echo "📦 可能需要安装依赖：pnpm install"
elif [[ "$ERROR_MESSAGE" =~ "TypeScript error" ]]; then
  echo "🔧 TypeScript错误，运行：workflow typecheck --fix"
elif [[ "$ERROR_MESSAGE" =~ "Test failed" ]]; then
  echo "🧪 测试失败，运行：workflow test --fix"
fi`,

      'after_session': `#!/bin/bash
# 会话结束后执行

echo "👋 会话即将结束，保存工作状态..."

# 保存当前上下文
workflow context save

# 生成会话总结
echo ""
echo "📊 本次会话总结："
workflow stats session --current

echo ""
echo "✅ 工作状态已保存，下次会话将自动恢复！"`
    };

    for (const [hookName, content] of Object.entries(defaultHooks)) {
      const hookPath = path.join(this.hooksDir, hookName);
      if (!(await fs.pathExists(hookPath))) {
        await fs.writeFile(hookPath, content);
        await fs.chmod(hookPath, '755'); // 确保可执行
        await this.log(`创建默认Hook: ${hookName}`);
      }
    }
  }

  /**
   * 加载配置
   */
  private async loadConfig(): Promise<void> {
    const configPath = path.join(this.claudeDir, 'hook-config.json');
    if (await fs.pathExists(configPath)) {
      try {
        const userConfig = await fs.readJson(configPath);
        this.config = { ...this.config, ...userConfig };
        await this.log('加载用户Hook配置');
      } catch (error) {
        await this.log('加载Hook配置失败，使用默认配置', { error });
      }
    } else {
      // 保存默认配置
      await fs.writeJson(configPath, this.config, { spaces: 2 });
      await this.log('创建默认Hook配置');
    }
  }

  /**
   * 记录日志
   */
  private async log(message: string, data?: any): Promise<void> {
    if (!this.config.logEnabled) return;

    const logEntry = {
      timestamp: new Date().toISOString(),
      message,
      data
    };

    try {
      await fs.appendFile(
        this.logFile,
        JSON.stringify(logEntry) + '\n'
      );
    } catch (error) {
      // 日志失败不应影响主流程
      console.error('Hook日志记录失败:', error);
    }
  }

  /**
   * 列出所有Hooks
   */
  async listHooks(): Promise<Hook[]> {
    return Object.values(this.config.hooks);
  }

  /**
   * 启用/禁用Hook
   */
  async setHookEnabled(hookName: string, enabled: boolean): Promise<void> {
    if (this.config.hooks[hookName]) {
      this.config.hooks[hookName].enabled = enabled;
      await this.saveConfig();
      await this.log(`Hook ${hookName} ${enabled ? '启用' : '禁用'}`);
    }
  }

  /**
   * 测试Hook
   */
  async testHook(hookName: string): Promise<void> {
    const testContext: HookContext = {
      type: 'test',
      timestamp: new Date(),
      data: { test: true }
    };

    logger.info(`🧪 测试Hook: ${hookName}`);
    await this.executeHook(hookName, testContext);
  }

  /**
   * 保存配置
   */
  private async saveConfig(): Promise<void> {
    const configPath = path.join(this.claudeDir, 'hook-config.json');
    await fs.writeJson(configPath, this.config, { spaces: 2 });
  }
}

// 导出单例
export const hookSystem = new ClaudeHookSystem();